import { useEffect, useState } from "react";
import {
	ChevronLeft,
	ChevronRight,
	Download,
	ExternalLink,
	FileText,
	Loader2,
	X,
} from "lucide-react";
import { toast } from "sonner";
import { cn } from "@/lib/utils";
import {
	Dialog,
	DialogContent,
	DialogTitle,
} from "@/components/ui/dialog";
import { type TaskAttachment } from "@/services/task-attachment.service";

function isImage(mime: string): boolean {
	return mime.startsWith("image/");
}

function isPdf(mime: string): boolean {
	return mime === "application/pdf";
}

function formatSize(bytes: number): string {
	if (bytes < 1024) return `${bytes} B`;
	if (bytes < 1024 * 1024) return `${Math.round(bytes / 1024)} KB`;
	return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}

async function downloadAttachment(att: TaskAttachment) {
	const res = await fetch(att.file_url);
	if (!res.ok) throw new Error("Failed to fetch file");
	const blob = await res.blob();
	const blobUrl = URL.createObjectURL(blob);
	const a = document.createElement("a");
	a.href = blobUrl;
	a.download = att.file_name;
	document.body.appendChild(a);
	a.click();
	a.remove();
	URL.revokeObjectURL(blobUrl);
}

interface Props {
	items: TaskAttachment[];
	index: number | null;
	onIndexChange: (i: number) => void;
	onClose: () => void;
}

export function AttachmentPreviewModal({
	items,
	index,
	onIndexChange,
	onClose,
}: Props) {
	const open = index !== null && index >= 0 && index < items.length;
	const [downloading, setDownloading] = useState(false);

	async function handleDownload(att: TaskAttachment) {
		setDownloading(true);
		try {
			await downloadAttachment(att);
		} catch (e) {
			toast.error("Download failed", {
				description: (e as Error).message,
			});
		} finally {
			setDownloading(false);
		}
	}

	useEffect(() => {
		if (!open || index === null) return;
		function onKey(e: KeyboardEvent) {
			if (e.key === "ArrowLeft" && index! > 0) {
				onIndexChange(index! - 1);
			} else if (e.key === "ArrowRight" && index! < items.length - 1) {
				onIndexChange(index! + 1);
			}
		}
		window.addEventListener("keydown", onKey);
		return () => window.removeEventListener("keydown", onKey);
	}, [open, index, items.length, onIndexChange]);

	if (!open || index === null) return null;

	const att = items[index];
	const hasPrev = index > 0;
	const hasNext = index < items.length - 1;

	return (
		<Dialog open={open} onOpenChange={(v) => !v && onClose()}>
			<DialogContent className="max-w-none w-screen h-screen p-0 rounded-none border-0 bg-black/95 text-white [&>button:last-child]:hidden">
				<DialogTitle className="sr-only">{att.file_name}</DialogTitle>

				{/* Top toolbar */}
				<div className="absolute inset-x-0 top-0 z-10 flex items-center justify-between gap-3 px-4 py-3 bg-gradient-to-b from-black/70 to-transparent">
					<div className="min-w-0 flex items-center gap-2 text-sm">
						<span className="truncate font-medium">
							{att.file_name}
						</span>
						{items.length > 1 && (
							<span className="shrink-0 text-white/60 text-xs">
								{index + 1} / {items.length}
							</span>
						)}
					</div>
					<div className="flex items-center gap-1.5 shrink-0">
						<a
							href={att.file_url}
							target="_blank"
							rel="noopener noreferrer"
							className="inline-flex items-center gap-1.5 rounded-md px-2.5 py-1.5 text-xs font-medium text-white/90 hover:bg-white/10 transition-colors"
						>
							<ExternalLink className="h-3.5 w-3.5" />
							View in new tab
						</a>
						<button
							type="button"
							onClick={() => handleDownload(att)}
							disabled={downloading}
							className="inline-flex items-center gap-1.5 rounded-md px-2.5 py-1.5 text-xs font-medium text-white/90 hover:bg-white/10 transition-colors disabled:opacity-60"
						>
							{downloading ? (
								<Loader2 className="h-3.5 w-3.5 animate-spin" />
							) : (
								<Download className="h-3.5 w-3.5" />
							)}
							Download
						</button>
						<button
							type="button"
							onClick={onClose}
							aria-label="Close preview"
							className="inline-flex items-center justify-center h-10 w-10 rounded-full text-white hover:bg-white/15 transition-colors"
						>
							<X className="h-6 w-6" />
						</button>
					</div>
				</div>

				{/* Stage */}
				<div className="absolute inset-0 flex items-center justify-center p-6 md:px-20">
					<AttachmentStage attachment={att} onDownload={() => handleDownload(att)} />
				</div>

				{/* Prev / next */}
				{items.length > 1 && (
					<>
						<NavButton
							side="left"
							disabled={!hasPrev}
							onClick={() => hasPrev && onIndexChange(index - 1)}
						/>
						<NavButton
							side="right"
							disabled={!hasNext}
							onClick={() => hasNext && onIndexChange(index + 1)}
						/>
					</>
				)}
			</DialogContent>
		</Dialog>
	);
}

function NavButton({
	side,
	disabled,
	onClick,
}: {
	side: "left" | "right";
	disabled: boolean;
	onClick: () => void;
}) {
	const Icon = side === "left" ? ChevronLeft : ChevronRight;
	return (
		<button
			type="button"
			onClick={onClick}
			disabled={disabled}
			aria-label={side === "left" ? "Previous attachment" : "Next attachment"}
			className={cn(
				"absolute top-1/2 -translate-y-1/2 z-10 h-11 w-11 rounded-full bg-white/10 text-white flex items-center justify-center hover:bg-white/20 transition-colors disabled:opacity-0 disabled:pointer-events-none",
				side === "left" ? "left-3" : "right-3",
			)}
		>
			<Icon className="h-6 w-6" />
		</button>
	);
}

function AttachmentStage({
	attachment,
	onDownload,
}: {
	attachment: TaskAttachment;
	onDownload: () => void;
}) {
	if (isImage(attachment.mime_type)) {
		return (
			<img
				src={attachment.file_url}
				alt={attachment.file_name}
				className="max-h-full max-w-full object-contain"
			/>
		);
	}

	if (isPdf(attachment.mime_type)) {
		return (
			<iframe
				src={attachment.file_url}
				title={attachment.file_name}
				className="h-full w-full max-w-5xl rounded border border-white/10 bg-white"
			/>
		);
	}

	return (
		<div className="flex flex-col items-center justify-center text-center gap-2 text-white/70">
			<FileText className="h-16 w-16" />
			<p className="text-sm font-medium text-white">
				{attachment.file_name}
			</p>
			<p className="text-xs">
				{formatSize(attachment.file_size)} · Preview not available
			</p>
			<button
				type="button"
				onClick={onDownload}
				className="text-xs text-primary hover:underline"
			>
				Download to view
			</button>
		</div>
	);
}
